"use client";

/**
 * @file 风格选择器组件
 * @description 提供多种风格选项供用户选择的组件
 */

import React from 'react';

/**
 * 风格选项接口
 */
interface StyleOption {
  /** 风格唯一标识符 */
  id: string;
  /** 风格名称 */
  name: string;
  /** 风格描述 */
  description: string;
}

/**
 * 风格选择器组件属性接口
 */
interface StyleSelectorProps {
  /** 可用的风格选项 */
  options: StyleOption[];
  /** 当前选择的风格 */
  selectedStyle: StyleOption;
  /** 风格选择回调函数 */
  onSelectStyle: (style: StyleOption) => void;
}

/**
 * 风格选择器组件
 * @param {StyleSelectorProps} props - 组件属性
 * @returns {JSX.Element} 风格选择器组件
 */
export default function StyleSelector({
  options,
  selectedStyle,
  onSelectStyle,
}: StyleSelectorProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((style) => (
          <div
            key={style.id}
            className={`border rounded-lg p-3 cursor-pointer transition-all ${
              selectedStyle.id === style.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
            }`}
            onClick={() => onSelectStyle(style)}
          >
            <div className="flex items-center">
              <div
                className={`w-4 h-4 rounded-full border ${
                  selectedStyle.id === style.id
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-400 bg-white dark:bg-gray-800'
                }`}
              >
                {selectedStyle.id === style.id && (
                  <div className="w-2 h-2 rounded-full bg-white m-auto" />
                )}
              </div>
              <span className="ml-2 font-medium">{style.name}</span>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 pl-6">
              {style.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <h3 className="font-medium text-sm text-gray-700 dark:text-gray-300">
          已选择: {selectedStyle.name}
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {selectedStyle.description}
        </p>
      </div>
    </div>
  );
}
